// @flow
import React from 'react';
import type { Node } from 'react';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { FileUploader } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Button,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { UploadIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import useIntl from '../../../hooks/useIntl';
import appMessages from '../../../containers/App/messages';
import { correctedLeaseUploadMessages as messages } from '../messages';
import {
  CORRECTED_LEASE_FILE_TYPES,
  CORRECTED_LEASE_MAX_SIZE_MB,
  useCorrectedLeaseUpload,
} from './hooks';

export { CORRECTED_LEASE_FILE_TYPES, CORRECTED_LEASE_MAX_SIZE_MB };

const MODAL_PROPS = {
  // The Edit Move-In Date modal shows again and focuses the row's button itself; the button
  // focused when this dialog opened is gone by then.
  disableRestoreFocus: true,
  // The width of the Edit Move-In Date modal it replaces (et-custom.css .modal-md).
  PaperProps: { sx: { width: { xs: 'calc(100% - 32px)', sm: 580 } } },
};

/**
 * The Upload Corrected Lease dialog of the Move-In Date correction (ENG-33327). It replaces the
 * Edit Move-In Date modal while open (no stacked modals, UL7): Cancel, Escape, the close icon or
 * the backdrop return to it with the held file unchanged; Save returns with the selection as the
 * held file (none when the held file was removed). Nothing is uploaded until the correction is
 * saved.
 */
const CorrectedLeaseUpload = ({
  show,
  heldFile,
  onCancel,
  onSave,
}: {
  show: boolean,
  heldFile: ?File,
  onCancel: () => void,
  onSave: (file: ?File) => void,
}): Node => {
  const intl = useIntl();
  const {
    selection,
    tooLargeFileName,
    uploaderKey,
    canSave,
    handleChange,
    handleSave,
  } = useCorrectedLeaseUpload({ open: show, heldFile, onSave });

  return (
    <Modal
      open={show}
      onClose={onCancel}
      title={intl.formatMessage(messages.title)}
      icon={<UploadIcon />}
      actions={
        <>
          <Button variant="text" onClick={onCancel}>
            {intl.formatMessage(appMessages.cancel)}
          </Button>
          <Button variant="primary" disabled={!canSave} onClick={handleSave}>
            {intl.formatMessage(appMessages.save)}
          </Button>
        </>
      }
      ModalProps={MODAL_PROPS}
    >
      <Typography variant="bodyMedium" component="p">
        {intl.formatMessage(messages.intro)}
      </Typography>
      {/* The dialog's content mounts with each opening, so the uploader starts from the held file. */}
      <FileUploader
        key={uploaderKey}
        multiple={false}
        requiredFileType={CORRECTED_LEASE_FILE_TYPES}
        initialFiles={selection ? [selection] : []}
        onChange={handleChange}
        sx={{ width: '100%', maxWidth: '100%', mt: 2 }}
      />
      {tooLargeFileName && (
        <Typography
          variant="bodySmall"
          color="error"
          component="p"
          data-test="corrected-lease-too-large"
          sx={{ mt: 1 }}
        >
          {intl.formatMessage(messages.fileTooLarge, {
            fileName: tooLargeFileName,
            maxSizeInMB: CORRECTED_LEASE_MAX_SIZE_MB,
          })}
        </Typography>
      )}
    </Modal>
  );
};

export default CorrectedLeaseUpload;
